import {
  Scene,
  Vector3,
  MeshBuilder,
  Color3,
  Mesh,
  PointLight,
  StandardMaterial,
} from "@babylonjs/core";
import { BaseZone } from "./BaseZone.js";
import type { MallStatItem, Shop, ShopProduct } from "@mall-mart/shared";
import { PRODUCTS_PER_SHOP, soldLine } from "@mall-mart/shared";
import { fetchProducts } from "../../api/shopsApi.js";
import { getMallStats } from "../../api/statsApi.js";
import { DynamicTextureSurface } from "../surfaces/DynamicTextureSurface.js";
import type { SurfaceTheme } from "../surfaces/types.js";
import { getSharedMaterials } from "../materials.js";
import { resizeShopifyImage } from "../imageUtils.js";
import { relativeSoldTime } from "../../util/relativeTime.js";
import { mergeStaticChildren } from "../mergeStatic.js";
import { addContactShadow } from "../props.js";
import { shouldReceiveShadow } from "../lighting.js";

export const UNIT_HALF_WIDTH: number = 6;
export const UNIT_DEPTH: number = 25;
export const UNIT_HEIGHT: number = 6;
const DOOR_WIDTH: number = 4;
// Shared with the server, which caps /api/shops/:id/products to the same
// number: what arrives is exactly what these slots can show.
const MAX_DISPLAYED_PRODUCTS: number = PRODUCTS_PER_SHOP;

/**
 * One parameterized shop unit. Local space: the facade sits on the corridor
 * wall line at z=0 (x in [-6, 6]); the room extends into local +z (0..16).
 * The zone's rotationY (set by mallLayout) points local +z away from the
 * corridor.
 *
 * The shell (facade, walls, sign) builds eagerly for every unit; the interior
 * (fixtures, product displays) is streamed in/out by StreamingManager.
 */
export class ShopUnitZone extends BaseZone {
  readonly shop: Shop | null;
  private readonly serverUrl: string;
  private readonly onProductClick: (shop: Shop, product: ShopProduct) => void;
  private readonly onCheckout: (shop: Shop) => void;
  private interiorRootMeshes: Array<{ dispose(): void }> = [];
  private interiorBuilt: boolean = false;
  private interiorLoading: boolean = false;
  private floor: Mesh | undefined;
  private theme: SurfaceTheme;

  constructor(
    scene: Scene,
    origin: Vector3,
    rotationY: number,
    shop: Shop | null,
    serverUrl: string,
    onProductClick: (shop: Shop, product: ShopProduct) => void,
    onCheckout: (shop: Shop) => void
  ) {
    super(shop === null ? `vacant-${origin.x.toFixed(0)}-${origin.z.toFixed(0)}` : `shop-${shop.id}`, scene, origin);
    this.root.rotation.y = rotationY;
    this.shop = shop;
    this.serverUrl = serverUrl;
    this.onProductClick = onProductClick;
    this.onCheckout = onCheckout;
    const t = shop?.theme;
    this.theme = {
      background: t?.background ?? "#2b2b31",
      foreground: t?.foreground ?? "#e8e6df",
      accent: shop?.accent ?? "#8f8f99",
      fontFamily: "sans-serif",
      fontFamilySerif: "serif",
    };
    this.entryPosition = new Vector3(origin.x, 1.7, origin.z);
  }

  /** Door position in world space (streaming distance checks). */
  doorWorldPosition(): Vector3 {
    return this.origin.clone();
  }

  async build(): Promise<void> {
    this.buildShell();
    mergeStaticChildren(this.root, (m) => m === this.floor);
  }

  buildShell(): void {
    const mats = getSharedMaterials(this.scene);
    const hw: number = UNIT_HALF_WIDTH;
    const d: number = UNIT_DEPTH;
    const h: number = UNIT_HEIGHT;
    const t: number = 0.3;
    const isVacant: boolean = this.shop === null;

    // Floor + ceiling
    const floor: Mesh = MeshBuilder.CreateGround(
      `${this.id}-floor`, { width: hw * 2, height: d }, this.scene
    );
    floor.position = new Vector3(0, 0.01, d / 2);
    floor.material = mats.shopFloor;
    floor.parent = this.root;
    floor.checkCollisions = true;
    this.floor = floor;

    const ceil: Mesh = MeshBuilder.CreateBox(
      `${this.id}-ceil`, { width: hw * 2, height: 0.2, depth: d }, this.scene
    );
    ceil.position = new Vector3(0, h, d / 2);
    ceil.material = mats.ceiling;
    ceil.parent = this.root;

    // Side + back walls
    const walls: Array<{ name: string; pos: Vector3; w: number; hh: number; dd: number }> = [
      { name: "back", pos: new Vector3(0, h / 2, d), w: hw * 2, hh: h, dd: t },
      { name: "left", pos: new Vector3(-hw, h / 2, d / 2), w: t, hh: h, dd: d },
      { name: "right", pos: new Vector3(hw, h / 2, d / 2), w: t, hh: h, dd: d },
    ];
    for (const c of walls) {
      const wall: Mesh = MeshBuilder.CreateBox(
        `${this.id}-wall-${c.name}`, { width: c.w, height: c.hh, depth: c.dd }, this.scene
      );
      wall.position = c.pos;
      wall.material = mats.shopWall;
      wall.parent = this.root;
      wall.checkCollisions = true;
    }

    // Facade: two solid piers + glass panels + doorway (or a closed front for vacant units)
    const pierW: number = (hw * 2 - DOOR_WIDTH) / 2; // 4
    if (isVacant) {
      const front: Mesh = MeshBuilder.CreateBox(
        `${this.id}-front`, { width: hw * 2, height: h, depth: t }, this.scene
      );
      front.position = new Vector3(0, h / 2, 0);
      front.material = mats.shopWall;
      front.parent = this.root;
      front.checkCollisions = true;
    } else {
      const accentMat: StandardMaterial = new StandardMaterial(`${this.id}-accent-mat`, this.scene);
      const accentColor: Color3 = Color3.FromHexString(this.shop?.accent ?? "#8f8f99");
      accentMat.diffuseColor = accentColor;
      accentMat.specularColor = new Color3(0.05, 0.05, 0.05);
      // Neon storefront: the accent trim glows in the shop's color.
      accentMat.emissiveColor = accentColor.scale(0.75);

      const glassH: number = h - 2.0 - 0.8;
      for (const side of [-1, 1]) {
        // Glass display panel over a low knee wall on each pier
        const knee: Mesh = MeshBuilder.CreateBox(
          `${this.id}-knee-${side}`, { width: pierW, height: 0.8, depth: t }, this.scene
        );
        knee.position = new Vector3(side * (DOOR_WIDTH / 2 + pierW / 2), 0.4, 0);
        knee.material = mats.shopWall;
        knee.parent = this.root;
        knee.checkCollisions = true;

        const glass: Mesh = MeshBuilder.CreateBox(
          `${this.id}-glass-${side}`, { width: pierW, height: glassH, depth: 0.06 }, this.scene
        );
        glass.position = new Vector3(side * (DOOR_WIDTH / 2 + pierW / 2), 0.8 + glassH / 2, 0);
        glass.material = mats.glass;
        glass.parent = this.root;
        glass.checkCollisions = true;

        // No vertical mullion here on purpose: the window product displays
        // hang at exactly this x, and a center bar bisects them from the
        // corridor. Modern frameless storefront glass needs no divider.
      }
      // Door frame posts + lintel around the entry opening
      for (const side of [-1, 1]) {
        const post: Mesh = MeshBuilder.CreateBox(
          `${this.id}-doorpost-${side}`, { width: 0.16, height: h - 2.0, depth: 0.18 }, this.scene
        );
        post.position = new Vector3(side * (DOOR_WIDTH / 2), (h - 2.0) / 2, 0);
        post.material = mats.frame;
        post.parent = this.root;
      }
      // Header band above door + glass
      const header: Mesh = MeshBuilder.CreateBox(
        `${this.id}-header`, { width: hw * 2, height: 2.0, depth: t }, this.scene
      );
      header.position = new Vector3(0, h - 1.0, 0);
      header.material = mats.shopWall;
      header.parent = this.root;

      // Accent trim strip along the base of the header
      const trim: Mesh = MeshBuilder.CreateBox(
        `${this.id}-trim`, { width: hw * 2, height: 0.14, depth: t + 0.06 }, this.scene
      );
      trim.position = new Vector3(0, h - 2.0, 0);
      trim.material = accentMat;
      trim.parent = this.root;

      // Sloped fabric awning over the doorway in the shop's accent color
      const awning: Mesh = MeshBuilder.CreateBox(
        `${this.id}-awning`, { width: DOOR_WIDTH + 0.8, height: 0.05, depth: 1.1 }, this.scene
      );
      awning.position = new Vector3(0, h - 2.15, -0.55);
      awning.rotation.x = -0.32; // slopes down toward the corridor
      awning.material = accentMat;
      awning.parent = this.root;
    }

    // Sign centered on the header (faces the corridor: local -z)
    const sign: DynamicTextureSurface = new DynamicTextureSurface({
      scene: this.scene,
      name: `${this.id}-sign`,
      widthM: 9,
      heightM: 1.6,
      resolution: { width: 1024, height: 192 },
      theme: this.theme,
      emissive: true,
    });
    sign.placeAt({
      position: new Vector3(0, UNIT_HEIGHT - 1.0, -0.18),
      rotationY: Math.PI,
      parent: this.root,
    });
    sign.setContent(
      this.shop === null
        ? { title: "Space Available", subtitle: "Lease with Mall Mart" }
        : { title: this.shop.name, subtitle: this.shop.tagline }
    );
  }

  /** Called by StreamingManager when the player approaches. */
  async loadInterior(): Promise<void> {
    if (this.interiorBuilt || this.interiorLoading || this.shop === null) {
      return;
    }
    this.interiorLoading = true;
    try {
      this.buildFixtures();
      const products: ShopProduct[] = await fetchProducts(this.serverUrl, this.shop.id);
      if (!this.interiorLoading) {
        return; // unloaded while fetching
      }
      this.renderProducts(prioritizeInStock(products).slice(0, MAX_DISPLAYED_PRODUCTS));
      this.interiorBuilt = true;
    } catch (err: unknown) {
      console.error(`[ShopUnit ${this.id}] Failed to load interior:`, err);
      this.addUnavailableNotice();
      this.interiorBuilt = true;
    } finally {
      this.interiorLoading = false;
    }
    // Streamed-in meshes must sample the static shadow map like the shell
    // does: without receiveShadows the skylight sun ADDS full daylight to
    // interiors it cannot actually reach (the shop ceiling occludes it in
    // the map rendered at load). No caster registration and no map refresh
    // needed for the same reason.
    for (const mesh of this.root.getChildMeshes()) {
      if (shouldReceiveShadow(mesh)) {
        mesh.receiveShadows = true;
      }
    }
  }

  /** Called by StreamingManager when the player walks far away. */
  unloadInterior(): void {
    this.interiorLoading = false;
    if (!this.interiorBuilt) {
      return;
    }
    for (const disposable of this.interiorRootMeshes) {
      disposable.dispose();
    }
    this.interiorRootMeshes = [];
    this.interiorBuilt = false;
  }

  isInteriorLoaded(): boolean {
    return this.interiorBuilt;
  }

  private track<T extends { dispose(): void }>(item: T): T {
    this.interiorRootMeshes.push(item);
    return item;
  }

  private buildFixtures(): void {
    const mats = getSharedMaterials(this.scene);
    const d: number = UNIT_DEPTH;

    // Two round display tables down the middle (drum base + overhanging top)
    for (let i: number = 0; i < 2; i++) {
      const drum: Mesh = this.track(
        MeshBuilder.CreateCylinder(`${this.id}-table-${i}`, { diameter: 1.7, height: 0.9, tessellation: 28 }, this.scene)
      );
      drum.position = new Vector3(0, 0.45, 8 + i * 8);
      drum.material = mats.woodFixture;
      drum.parent = this.root;
      drum.checkCollisions = true;

      const top: Mesh = this.track(
        MeshBuilder.CreateCylinder(`${this.id}-tabletop-${i}`, { diameter: 2.1, height: 0.07, tessellation: 32 }, this.scene)
      );
      top.position = new Vector3(0, 0.93, 8 + i * 8);
      top.material = mats.metalFixture;
      top.parent = this.root;

      this.track(
        addContactShadow(this.scene, this.root, `${this.id}-table-${i}`, new Vector3(0, 0, 8 + i * 8), 1.2)
      );
    }

    // Warm interior light
    const light: PointLight = this.track(
      new PointLight(`${this.id}-light`, new Vector3(0, UNIT_HEIGHT - 1, d / 2), this.scene)
    );
    light.diffuse = Color3.FromHexString(this.shop?.accent ?? "#ffddaa");
    light.diffuse = Color3.Lerp(light.diffuse, new Color3(1, 0.92, 0.8), 0.7);
    light.intensity = 0.7;
    light.range = 20;
    light.parent = this.root;

    // Checkout plaque on the back wall
    const checkout: DynamicTextureSurface = this.track(
      new DynamicTextureSurface({
        scene: this.scene,
        name: `${this.id}-checkout`,
        widthM: 3,
        heightM: 1,
        resolution: { width: 512, height: 170 },
        theme: this.theme,
        emissive: true,
      })
    );
    checkout.placeAt({
      position: new Vector3(0, 1.6, d - 0.35),
      rotationY: Math.PI,
      parent: this.root,
    });
    checkout.setContent({ title: "CHECKOUT", subtitle: "tap to pay" });
    checkout.onPick(() => {
      if (this.shop !== null) {
        this.onCheckout(this.shop);
      }
    });
  }

  private renderProducts(products: ShopProduct[]): void {
    if (this.shop === null) {
      return;
    }
    const hw: number = UNIT_HALF_WIDTH;
    const d: number = UNIT_DEPTH;
    // 20 display slots: 2 window displays facing the corridor, 4 back wall,
    // 7 left, 7 right. The deeper room (UNIT_DEPTH) gives the side walls room
    // for 7 displays each at the same 3.4 m spacing.
    const slots: Array<{ pos: Vector3; rotY: number }> = [];
    for (const side of [-1, 1]) {
      // Just inside the storefront glass, readable from the corridor.
      slots.push({ pos: new Vector3(side * 4, 2.2, 0.5), rotY: Math.PI });
    }
    for (let i: number = 0; i < 4; i++) {
      const x: number = -4.2 + i * 2.8;
      // The CHECKOUT plaque sits center back wall (x -1.5..1.5, up to y 2.1);
      // the two middle displays hang above it so they never cover it.
      const y: number = Math.abs(x) < 2 ? 4.2 : 2.4;
      slots.push({ pos: new Vector3(x, y, d - 0.4), rotY: Math.PI });
    }
    for (let i: number = 0; i < 7; i++) {
      const z: number = 3 + i * 3.4;
      // Left wall: displays face +x into the room.
      slots.push({ pos: new Vector3(-hw + 0.4, 2.4, z), rotY: Math.PI / 2 });
    }
    for (let i: number = 0; i < 7; i++) {
      const z: number = 3 + i * 3.4;
      // Right wall: displays face -x into the room.
      slots.push({ pos: new Vector3(hw - 0.4, 2.4, z), rotY: -Math.PI / 2 });
    }

    const tiles: DisplayTile[] = buildDisplayTiles(
      products,
      getMallStats()?.bestSellersByShop[this.shop.id],
      getMallStats()?.unitsSoldByShop[this.shop.id] ?? {}
    );

    this.buildTableHolograms(products);

    for (let i: number = 0; i < tiles.length && i < slots.length; i++) {
      const tile: DisplayTile = tiles[i];
      const surface: DynamicTextureSurface = this.track(
        new DynamicTextureSurface({
          scene: this.scene,
          name: `product-${this.id}-${i}`,
          widthM: 2.4,
          heightM: 2.9,
          resolution: { width: 512, height: 620 },
          theme: this.theme,
          emissive: true,
          layout: "imageTop",
          singleSided: true,
          excludeFromGlow: true,
        })
      );
      surface.placeAt({ position: slots[i].pos, rotationY: slots[i].rotY, parent: this.root });
      surface.setContent({
        title: truncate(tile.title, 22),
        imageUrl: resizeShopifyImage(tile.imageUrl, 512),
        bodyLines: tile.bodyLines,
        ctaText: tile.ctaText,
      });
      const pickProduct: ShopProduct | undefined = tile.product;
      if (pickProduct !== undefined) {
        surface.onPick(() => this.onProductClick(this.shop as Shop, pickProduct));
      }

      // Frame behind the display
      const frame: Mesh = this.track(
        MeshBuilder.CreateBox(`frame-${this.id}-${i}`, { width: 2.6, height: 3.1, depth: 0.08 }, this.scene)
      );
      const mats = getSharedMaterials(this.scene);
      frame.material = mats.frame;
      frame.parent = this.root;
      // Push the frame opposite the display's facing so it sits behind it.
      const back: number = 0.06;
      frame.position = new Vector3(
        slots[i].pos.x - Math.sin(slots[i].rotY) * back,
        slots[i].pos.y,
        slots[i].pos.z - Math.cos(slots[i].rotY) * back
      );
      frame.rotation.y = slots[i].rotY;
    }
  }

  /** Little spinning "hologram" tiles floating over the two round center
   *  tables: this store's most-sold item on one, and the mall's last-sold
   *  item on the other when it belongs to this store. */
  private buildTableHolograms(products: ShopProduct[]): void {
    if (this.shop === null) {
      return;
    }
    const stats = getMallStats();
    const holos: TableHologram[] = pickTableHolograms(
      products,
      stats?.bestSellersByShop[this.shop.id],
      stats?.lastSoldByShop[this.shop.id],
      stats?.lastSold,
      stats?.unitsSoldByShop[this.shop.id] ?? {}
    );
    // Table drums sit at z=8 and z=16; tops finish at y≈0.97.
    const tableZ: number[] = [8, 16];
    for (let i: number = 0; i < holos.length && i < tableZ.length; i++) {
      const holo: TableHologram = holos[i];

      const surface: DynamicTextureSurface = this.track(
        new DynamicTextureSurface({
          scene: this.scene,
          name: `${this.id}-holo-${i}`,
          widthM: 1.1,
          heightM: 1.35,
          resolution: { width: 256, height: 314 },
          theme: this.theme,
          emissive: true,
          layout: "imageTop",
          excludeFromGlow: true,
          opacity: 0.82,
        })
      );
      surface.placeAt({
        position: new Vector3(0, 1.75, tableZ[i]),
        rotationY: 0,
        parent: this.root,
      });
      const soldLine: string[] =
        holo.item.unitsSold !== undefined ? [`${holo.item.unitsSold} sold`] : [];
      const soldAgo: string | null = relativeSoldTime(holo.item.soldAt);
      if (soldAgo !== null) {
        soldLine.push(`Bought ${soldAgo}`);
      }
      surface.setContent({
        title: truncate(holo.item.title, 20),
        imageUrl: resizeShopifyImage(holo.item.imageUrl, 256),
        bodyLines: soldLine,
        ctaText: holo.label,
      });
      const pickProduct: ShopProduct | undefined = holo.product;
      if (pickProduct !== undefined) {
        surface.onPick(() => this.onProductClick(this.shop as Shop, pickProduct));
      }

      // Slow spin sells the hologram effect.
      const observer = this.scene.onBeforeRenderObservable.add(() => {
        surface.mesh.rotation.y += this.scene.getEngine().getDeltaTime() * 0.0006;
      });
      this.track({ dispose: () => this.scene.onBeforeRenderObservable.remove(observer) });

      // Emissive projector disc on the tabletop under the hologram.
      const disc: Mesh = this.track(
        MeshBuilder.CreateCylinder(
          `${this.id}-holo-disc-${i}`,
          { diameter: 0.7, height: 0.03, tessellation: 24 },
          this.scene
        )
      );
      disc.position = new Vector3(0, 0.99, tableZ[i]);
      const discMat: StandardMaterial = this.track(
        new StandardMaterial(`${this.id}-holo-disc-mat-${i}`, this.scene)
      );
      const accent: Color3 = Color3.FromHexString(this.shop.accent ?? "#3fb6a8");
      discMat.emissiveColor = accent.scale(0.8);
      discMat.diffuseColor = accent.scale(0.2);
      discMat.specularColor = new Color3(0, 0, 0);
      disc.material = discMat;
      disc.parent = this.root;
    }
  }

  private addUnavailableNotice(): void {
    const notice: DynamicTextureSurface = this.track(
      new DynamicTextureSurface({
        scene: this.scene,
        name: `${this.id}-unavailable`,
        widthM: 5,
        heightM: 1.6,
        resolution: { width: 640, height: 200 },
        theme: this.theme,
        emissive: true,
      })
    );
    notice.placeAt({
      position: new Vector3(0, 2.2, UNIT_DEPTH / 2),
      rotationY: Math.PI,
      parent: this.root,
    });
    notice.setContent({ title: "Temporarily unavailable", subtitle: "please check back soon" });
  }

  override floorMeshes(): Mesh[] {
    return this.floor === undefined ? [] : [this.floor];
  }

  override dispose(): void {
    this.unloadInterior();
    super.dispose();
  }
}

export interface DisplayTile {
  title: string;
  imageUrl: string;
  bodyLines: string[];
  ctaText: string;
  /** Product to open in the panel on click; undefined = not clickable. */
  product?: ShopProduct;
}

/**
 * Build the display tiles for a shop unit. When the mall stats know this
 * store's most-bought item, it takes the first window slot as a highlighted
 * BEST SELLER tile (linked to the matching product when one is found);
 * regular product tiles fill the remaining slots.
 */
export function buildDisplayTiles(
  products: ShopProduct[],
  bestSeller: MallStatItem | undefined,
  unitsSold: Record<string, number>
): DisplayTile[] {
  const tiles: DisplayTile[] = [];
  if (bestSeller !== undefined) {
    const match: ShopProduct | undefined = matchProduct(products, bestSeller);
    const soldLine: string[] =
      bestSeller.unitsSold !== undefined ? [`${bestSeller.unitsSold} sold`] : [];
    tiles.push({
      title: bestSeller.title,
      imageUrl: bestSeller.imageUrl,
      bodyLines: soldLine,
      ctaText: "BEST SELLER",
      product: match,
    });
  }
  for (const product of products) {
    const inStock: boolean = product.availability === "in_stock";
    const priceText: string =
      product.priceRange.min === product.priceRange.max
        ? `$${product.priceRange.min}`
        : `$${product.priceRange.min} to $${product.priceRange.max}`;
    // Only two body lines get drawn. A sold-out tile spends the second on the
    // stock notice: whether you can buy it beats how many others did.
    const second: string = inStock ? soldLine(product, unitsSold) : "Out of stock";
    tiles.push({
      title: product.title,
      imageUrl: product.images[0]?.url ?? "",
      bodyLines: [priceText, second],
      ctaText: inStock ? "VIEW" : "SOLD OUT",
      product,
    });
  }
  return tiles;
}


export interface TableHologram {
  /** CTA strip label on the hologram tile, e.g. "MOST SOLD". */
  label: string;
  item: MallStatItem;
  /** Product to open in the panel on click; undefined = not clickable. */
  product?: ShopProduct;
}

/** Match a mall stat item back to one of this shop's products. */
export function matchProduct(
  products: ShopProduct[],
  item: MallStatItem
): ShopProduct | undefined {
  const wanted: string = item.title.trim().toLowerCase();
  return products.find(
    (p) => p.title.trim().toLowerCase() === wanted || p.productUrl === item.url
  );
}

/**
 * Pick what the center-table holograms show. Table one: the store's
 * most-sold item from the per-store stats, or its own top-ranked product
 * when absent (server lists are mart-score sorted). Table two: this store's
 * last item sold (per-store stat, preferred), else the mall-wide last-sold
 * when it matches one of this store's products.
 */
export function pickTableHolograms(
  products: ShopProduct[],
  mostSold: MallStatItem | undefined,
  storeLastSold: MallStatItem | undefined,
  mallLastSold: MallStatItem | null | undefined,
  unitsSold: Record<string, number>
): TableHologram[] {
  const holos: TableHologram[] = [];
  if (mostSold !== undefined) {
    holos.push({ label: "MOST SOLD", item: mostSold, product: matchProduct(products, mostSold) });
  } else if (products.length > 0) {
    const top: ShopProduct = products[0];
    // The fallback branch has no stat item to read a count from, so take it
    // from the per-product map; absent means it has not sold and stays absent.
    const handle: string | undefined = top.handle;
    const units: number = handle === undefined ? 0 : (unitsSold[handle] ?? 0);
    const item: MallStatItem = {
      title: top.title,
      imageUrl: top.images[0]?.url ?? "",
      url: top.productUrl,
    };
    if (units > 0) {
      item.unitsSold = units;
    }
    holos.push({ label: "MOST BOUGHT", item, product: top });
  }
  if (storeLastSold !== undefined) {
    holos.push({
      label: "JUST SOLD",
      item: storeLastSold,
      product: matchProduct(products, storeLastSold),
    });
  } else if (mallLastSold !== null && mallLastSold !== undefined) {
    const match: ShopProduct | undefined = matchProduct(products, mallLastSold);
    if (match !== undefined) {
      holos.push({ label: "JUST SOLD", item: mallLastSold, product: match });
    }
  }
  return holos;
}

/**
 * Display slots are limited, so sold-out items must not hold a slot while
 * in-stock items wait. Stable partition: in-stock first (keeping the server's
 * ranking order), out-of-stock only fills leftover slots.
 */
export function prioritizeInStock(products: ShopProduct[]): ShopProduct[] {
  const inStock: ShopProduct[] = products.filter((p) => p.availability === "in_stock");
  const outOfStock: ShopProduct[] = products.filter((p) => p.availability !== "in_stock");
  return [...inStock, ...outOfStock];
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}
